import React from "react";
import { useTranslation } from "react-i18next";
import { TrendingDown } from "lucide-react";
import { ReactComponent as Cursor02Icon } from "@icons/cursor-02.svg?react";
import { formatCurrency } from "@/utils/formatters";
import { cn } from "@hipotecar/ui/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * True when this component is rendered inside a modal Dialog/Sheet (e.g. the
 * mortgage detail drawer). A Radix Popover inside the modal froze the tab: its
 * focus scope fought the dialog's focus trap, and the drawer's slide `transform`
 * broke the popover's `position: fixed`, sending Floating UI into a recompute
 * loop. So in that case each StatsCard swaps the Popover for a plain
 * state-driven panel (no portal, no Floating UI, no focus trap).
 */
const InModalContext = React.createContext(false);

export interface MortgageStatsData {
	termMonths: number;
	totalPayment: number;
	totalInterest: number;
	totalInsurance: number;
	monthlyPayment: number;
}

interface MortgageImpactStatsProps {
	current: MortgageStatsData;
	baseline?: MortgageStatsData;
	currency: string;
	className?: string;
	headerAction?: React.ReactNode;
	/** Optional content rendered on the left of the header row (e.g. advice banner). */
	headerLeading?: React.ReactNode;
	isOriginal?: boolean;
	/** Set when rendered inside a modal Dialog/Sheet — suppresses popover auto-focus. */
	inModal?: boolean;
}

/** Months → years string (e.g. 240 → "20", 226 → "18.8"); empty below 12 months. */
const formatYears = (months: number): string => {
	if (months < 12) return "";
	const years = Math.round((months / 12) * 10) / 10;
	return Number.isInteger(years) ? `${years}` : years.toFixed(1);
};

export default function MortgageImpactStats({
	current,
	baseline,
	currency,
	className,
	headerAction,
	headerLeading,
	inModal = false,
}: MortgageImpactStatsProps) {
	const { t } = useTranslation(["hipotecas", "common"]);

	// Calculate Savings & Diffs
	const monthsSaved = Math.max(
		0,
		(baseline?.termMonths || current.termMonths) - current.termMonths,
	);

	// Financials
	const monthlyPaymentSaved = Math.max(
		0,
		(baseline?.monthlyPayment || current.monthlyPayment) -
			current.monthlyPayment,
	);
	const totalInterestSaved = Math.max(
		0,
		(baseline?.totalInterest || current.totalInterest) - current.totalInterest,
	);
	const insuranceSaved = Math.max(
		0,
		(baseline?.totalInsurance || current.totalInsurance) -
			current.totalInsurance,
	);
	const totalPaymentSaved = Math.max(
		0,
		totalInterestSaved + insuranceSaved,
	);

	const yearsUnit = t("view.years");
	const withYears = (months: number, unit: string) => {
		const y = formatYears(months);
		return y ? `${months} ${unit} (${y} ${yearsUnit})` : `${months} ${unit}`;
	};

	return (
		<InModalContext.Provider value={inModal}>
		<div className={cn("@container w-full space-y-2.5", className)}>
			{/* Header Row: Banner + Action */}
			<div className="flex w-full flex-col gap-3 @md:flex-row @md:items-center @md:justify-between">
				<div className="flex flex-col gap-1.5">
					{headerLeading}
				</div>
				{headerAction && <div className="shrink-0">{headerAction}</div>}
			</div>

			{/* Stats Cards Grid */}
			<div className="grid w-full grid-cols-1 gap-2 @md:grid-cols-2">
				{/* 1. Cuota Mensual */}
				<StatsCard
					title={t("charts.amortizationSchedule.monthlyPayment")}
					value={formatCurrency(current.monthlyPayment, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					trend={monthlyPaymentSaved > 0.01 ? `-${formatCurrency(monthlyPaymentSaved, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : undefined}
					className="col-span-1 border-none @md:col-span-2"
					hasSavings={monthlyPaymentSaved > 0.01}
					popupInitialLabel={t("hipotecas:amortization.popup_labels.monthly_payment_initial", "Cuota Inicial")}
					popupInitialValue={baseline ? formatCurrency(baseline.monthlyPayment, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 }) : ""}
					popupSavingsValue={formatCurrency(monthlyPaymentSaved, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					popupCurrentLabel={t("hipotecas:amortization.popup_labels.monthly_payment_current", "Cuota Mensual")}
					popupCurrentValue={formatCurrency(current.monthlyPayment, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					metricType="cuota"
				/>

				{/* 2. Plazo */}
				<StatsCard
					title={t("view.term")}
					value={
						<>
							{current.termMonths} {t("view.months")}
							{monthsSaved === 0 && formatYears(current.termMonths) && (
								<span className="ml-1.5 text-sm text-gray-400 dark:text-slate-400">
									({formatYears(current.termMonths)} {yearsUnit})
								</span>
							)}
						</>
					}
					trend={monthsSaved > 0 ? `${monthsSaved} ${t("view.months")}` : undefined}
					className="col-span-1 border-none"
					hasSavings={monthsSaved > 0}
					popupInitialLabel={t("hipotecas:amortization.popup_labels.term_initial", "Plazo inicial")}
					popupInitialValue={baseline ? withYears(baseline.termMonths, "m") : ""}
					popupSavingsValue={withYears(monthsSaved, "m")}
					popupCurrentLabel={t("hipotecas:amortization.popup_labels.term_current", "Plazo")}
					popupCurrentValue={withYears(current.termMonths, "m")}
					metricType="plazo"
				/>


				{/* 3. Pago Total */}
				<StatsCard
					title={t("charts.amortizationSchedule.totalPayment")}
					value={formatCurrency(current.totalPayment, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					trend={totalPaymentSaved > 10 ? `-${formatCurrency(totalPaymentSaved, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : undefined}
					className="col-span-1 border-none"
					hasSavings={totalPaymentSaved > 10}
					popupInitialLabel={t("hipotecas:amortization.popup_labels.total_payment_initial", "Pago Inicial")}
					popupInitialValue={baseline ? formatCurrency(baseline.totalPayment, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 }) : ""}
					popupSavingsValue={formatCurrency(totalPaymentSaved, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					popupCurrentLabel={t("hipotecas:amortization.popup_labels.total_payment_current", "Pago Total")}
					popupCurrentValue={formatCurrency(current.totalPayment, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					metricType="general"
				/>

				{/* 4. Total Intereses */}
				<StatsCard
					title={t("charts.amortizationSchedule.totalInterest")}
					value={formatCurrency(current.totalInterest, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					trend={totalInterestSaved > 10 ? `-${formatCurrency(totalInterestSaved, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : undefined}
					className="col-span-1 border-none"
					hasSavings={totalInterestSaved > 10}
					popupInitialLabel={t("hipotecas:amortization.popup_labels.total_interest_initial", "Intereses Iniciales")}
					popupInitialValue={baseline ? formatCurrency(baseline.totalInterest, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 }) : ""}
					popupSavingsValue={formatCurrency(totalInterestSaved, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					popupCurrentLabel={t("hipotecas:amortization.popup_labels.total_interest_current", "Total Intereses")}
					popupCurrentValue={formatCurrency(current.totalInterest, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					metricType="general"
				/>

				{/* 5. Seguros y Comisiones */}
				<StatsCard
					title={t("charts.amortizationSchedule.totalInsuranceFees")}
					value={formatCurrency(current.totalInsurance, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					trend={insuranceSaved > 10 ? `-${formatCurrency(insuranceSaved, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : undefined}
					className="col-span-1 border-none"
					hasSavings={insuranceSaved > 10}
					popupInitialLabel={t("hipotecas:amortization.popup_labels.total_insurance_initial", "Seguros Iniciales")}
					popupInitialValue={baseline ? formatCurrency(baseline.totalInsurance, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 }) : ""}
					popupSavingsValue={formatCurrency(insuranceSaved, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					popupCurrentLabel={t("hipotecas:amortization.popup_labels.total_insurance_current", "Seguros y Comisiones")}
					popupCurrentValue={formatCurrency(current.totalInsurance, currency, { showSymbol: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })}
					metricType="general"
				/>
			</div>
		</div>
		</InModalContext.Provider>
	);
}

interface StatsCardProps {
	title: string;
	value: React.ReactNode;
	trend?: string;
	className?: string;
	hasSavings: boolean;
	popupInitialLabel: string;
	popupInitialValue: string;
	popupSavingsValue: string;
	popupCurrentLabel: string;
	popupCurrentValue: string;
	metricType: "cuota" | "plazo" | "general";
}

function StatsCard({
	title,
	value,
	trend,
	className,
	hasSavings,
	popupInitialLabel,
	popupInitialValue,
	popupSavingsValue,
	popupCurrentLabel,
	popupCurrentValue,
	metricType,
}: StatsCardProps) {
	const { t } = useTranslation("hipotecas");
	const inModal = React.useContext(InModalContext);
	const [open, setOpen] = React.useState(false);

	// Circular bordered button matching the Figma design (rounded-full + border).
	const triggerClassName =
		"flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:border-slate-700 dark:hover:bg-gray-800 dark:hover:text-gray-200";
	const panelClassName =
		"w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-slate-700/70 dark:bg-slate-800";

	const breakdown = hasSavings ? (
		<div className="space-y-2 text-gray-700 dark:text-slate-300">
			{/* Row 1: Initial Value */}
			<div className="flex justify-between text-xs font-normal text-gray-500 dark:text-slate-400">
				<span>{popupInitialLabel}</span>
				<span className="font-semibold text-gray-900 dark:text-slate-200">{popupInitialValue}</span>
			</div>
			{/* Row 2: Savings (Ahorro) */}
			<div className="flex justify-between text-xs font-normal text-gray-500 dark:text-slate-400">
				<span>{t("amortization.savings_column", "Ahorro")}</span>
				<span className="font-semibold text-emerald-500 dark:text-emerald-400">-{popupSavingsValue}</span>
			</div>
			{/* Divider */}
			<div className="my-1.5 border-t border-gray-200 dark:border-slate-700" />
			{/* Row 3: Current Value */}
			<div className="flex justify-between text-xs font-normal text-gray-500 dark:text-slate-400">
				<span>{popupCurrentLabel}</span>
				<span className="font-semibold text-gray-900 dark:text-slate-200">{popupCurrentValue}</span>
			</div>
		</div>
	) : (
		<div className="text-xs/normal font-normal text-gray-500 dark:text-slate-400">
			{metricType === "cuota"
				? t("amortization.popup_labels.no_savings_cuota", "Sin ahorro, no tienes amortizaciones a cuota")
				: metricType === "plazo"
					? t("amortization.popup_labels.no_savings_plazo", "Sin ahorro, no tienes amortizaciones a plazo")
					: t("amortization.popup_labels.no_savings_general", "Sin ahorro, no tienes amortizaciones")}
		</div>
	);

	return (
		<div
			className={cn(
				"relative flex flex-col justify-between rounded-2xl border border-gray-300 bg-white px-4 py-2 shadow-sm dark:border-gray-700 dark:bg-card",
				className
			)}
		>
			<div className="space-y-0">
				{/* Header row */}
				<div className="flex items-start justify-between gap-2">
					<span className="text-xs font-medium text-gray-500 dark:text-slate-400">
						{title}
					</span>

					{inModal ? (
						/* Inside the modal drawer we render the breakdown INLINE (below,
						   in flow) instead of a floating popover/panel: a Radix Popover
						   freezes the tab (focus-trap fight + the drawer's slide transform
						   breaks fixed positioning), and an absolute panel ended up
						   invisible AND swallowing clicks. Inline can do neither. */
						<button
							type="button"
							aria-label="Expandir"
							aria-expanded={open}
							onClick={() => setOpen((o) => !o)}
							className={triggerClassName}
						>
							<Cursor02Icon className="size-4" aria-hidden />
						</button>
					) : (
						<Popover>
							<PopoverTrigger asChild>
								<button type="button" aria-label="Expandir" className={triggerClassName}>
									<Cursor02Icon className="size-4" aria-hidden />
								</button>
							</PopoverTrigger>
							<PopoverContent
								side="bottom"
								align="start"
								sideOffset={8}
								avoidCollisions
								collisionPadding={16}
								className={panelClassName}
							>
								{breakdown}
							</PopoverContent>
						</Popover>
					)}
				</div>

				{/* Value & Trend */}
				<div className="-mt-4 flex items-baseline gap-2 whitespace-nowrap">
					<span className="text-lg text-gray-900 dark:text-white">
						{value}
					</span>
					{trend && (
						<span className="inline-flex items-center gap-0.5 text-sm font-medium text-emerald-500 dark:text-emerald-400">
							<TrendingDown className="size-4 shrink-0" aria-hidden />
							<span className="whitespace-nowrap">{trend}</span>
						</span>
					)}
				</div>

				{/* Inline breakdown (modal/drawer): in-flow, never floats or overlays. */}
				{inModal && open && (
					<div className="mt-3 border-t border-gray-200 pt-3 dark:border-slate-700">
						{breakdown}
					</div>
				)}
			</div>
		</div>
	);
}
